import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as _ from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class Form8865TextualService {

  constructor(private http: HttpClient) { }

  /**
   * Build HTTP params for GET requests (matching AngularJS JsonObjectFactory.getJSONObj)
   * AngularJS uses $http.get(_url, {params: params}) which sends params as query string
   */
  private buildHttpParams(params: any): HttpParams {
    let httpParams = new HttpParams();
    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        const value = params[key];
        if (value !== null && value !== undefined) {
          httpParams = httpParams.set(key, 
            typeof value === 'object' ? JSON.stringify(value) : String(value)
          );
        }
      }
    }
    return httpParams;
  }

  /**
   * Build form data for POST requests (for save operations)
   */
  private buildFormData(params: any): string {
    const formData: string[] = [];
    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        const value = params[key];
        if (value !== null && value !== undefined) {
          formData.push(encodeURIComponent(key) + '=' + encodeURIComponent(
            typeof value === 'object' ? JSON.stringify(value) : value
          ));
        }
      }
    }
    return formData.join('&');
  }

  /**
   * Get POST HTTP options
   */
  private getPostHttpOptions() {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      }),
      withCredentials: true
    };
  }

  /**
   * Load Filing Group data
   * Action code: f5sne6
   * Original: JsonObjectFactory.getJSONObj uses GET
   */
  getFilingGroup(
    baseUrl: string,
    params: any,
    globalService: any,
    filterParams?: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject`;
    
    const mergedParams = {
      ...globalService,
      ...(filterParams || {}),
      ...params,
      action_code: 'f5sne6'
    };

    return this.http.get(url, { 
      params: this.buildHttpParams(mergedParams),
      withCredentials: true
    });
  }

  /**
   * Load Category Filer Data
   * Action code: 6flbbx
   */
  getCatgFilerData(
    baseUrl: string,
    params: any,
    globalService: any,
    filterParams?: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject`;
    
    const mergedParams = {
      ...globalService,
      ...(filterParams || {}),
      ...params,
      action_code: '6flbbx'
    };

    return this.http.get(url, { 
      params: this.buildHttpParams(mergedParams),
      withCredentials: true
    });
  }

  /**
   * Load Principal Business Activity
   * Action code: y5ypqx
   */
  getPrincipalBusinessActivity(
    baseUrl: string,
    globalService: any,
    filterParams?: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject`;
    
    const mergedParams = {
      ...globalService,
      ...(filterParams || {}),
      action_code: 'y5ypqx'
    };

    return this.http.get(url, { 
      params: this.buildHttpParams(mergedParams),
      withCredentials: true
    });
  }

  /**
   * Load Partner Details
   * Action code: x4g3o2
   */
  getPartnerDetails(
    baseUrl: string,
    globalService: any,
    filterParams?: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject`;
    
    const mergedParams = {
      ...globalService,
      ...(filterParams || {}),
      action_code: 'x4g3o2'
    };

    return this.http.get(url, { 
      params: this.buildHttpParams(mergedParams),
      withCredentials: true
    });
  }

  /**
   * Load Form Data (Schedule NA)
   * Action code: 0kploi
   */
  getFormData(
    baseUrl: string,
    params: any,
    globalService: any,
    filterParams?: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject`;
    
    const mergedParams = {
      ...globalService,
      ...(filterParams || {}),
      ...params,
      action_code: '0kploi'
    };

    return this.http.get(url, { 
      params: this.buildHttpParams(mergedParams),
      withCredentials: true
    });
  }

  /**
   * Load Countries
   * Action code: kbuwei
   */
  getCountries(
    baseUrl: string,
    params: any,
    globalService: any,
    filterParams?: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject`;
    
    const mergedParams = {
      ...globalService,
      ...(filterParams || {}),
      ...params,
      action_code: 'kbuwei'
    };

    return this.http.get(url, { 
      params: this.buildHttpParams(mergedParams),
      withCredentials: true
    });
  }

  /**
   * Load States
   * Action code: 6eyqnz
   */
  getStates(
    baseUrl: string,
    params: any,
    globalService: any,
    filterParams?: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject`;
    
    const mergedParams = {
      ...globalService,
      ...(filterParams || {}),
      ...params,
      action_code: '6eyqnz'
    };

    return this.http.get(url, { 
      params: this.buildHttpParams(mergedParams),
      withCredentials: true
    });
  }

  /**
   * Load Currencies
   * Action code: 3ucwjb (corrected from original AngularJS)
   */
  getCurrencies(
    baseUrl: string,
    params: any,
    globalService: any,
    filterParams?: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject`;
    
    const mergedParams = {
      ...globalService,
      ...(filterParams || {}),
      ...params,
      action_code: '3ucwjb'
    };

    return this.http.get(url, { 
      params: this.buildHttpParams(mergedParams),
      withCredentials: true
    });
  }

  /**
   * Load Schedule PG1 Data
   * Action code: 0kq750 (correct action code from AngularJS)
   */
  getSchedulePG1Data(
    baseUrl: string,
    params: any,
    globalService: any,
    filterParams?: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject`;
    
    const mergedParams = {
      ...globalService,
      ...(filterParams || {}),
      ...params,
      action_code: '0kq750'
    };

    return this.http.get(url, { 
      params: this.buildHttpParams(mergedParams),
      withCredentials: true
    });
  }

  /**
   * Load Schedule O Data
   * Action code: e9c4ho
   */
  getScheduleOData(
    baseUrl: string,
    params: any,
    globalService: any,
    filterParams?: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject`;
    
    const mergedParams = {
      ...globalService,
      ...(filterParams || {}),
      ...params,
      action_code: 'e9c4ho'
    };

    return this.http.get(url, { 
      params: this.buildHttpParams(mergedParams),
      withCredentials: true
    });
  }

  /**
   * Load Filer Info
   * Action code: bcuiwa
   */
  getFilerInfo(
    baseUrl: string,
    params: any,
    globalService: any,
    filterParams?: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject`;
    
    const mergedParams = {
      ...globalService,
      ...(filterParams || {}),
      ...params,
      action_code: 'bcuiwa'
    };

    return this.http.get(url, { 
      params: this.buildHttpParams(mergedParams),
      withCredentials: true
    });
  }

  /**
   * Load Partner Info
   * Action code: j6fgkm
   */
  getPartnerInfo(
    baseUrl: string,
    params: any,
    globalService: any,
    filterParams?: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject`;
    
    const mergedParams = {
      ...globalService,
      ...(filterParams || {}),
      ...params,
      action_code: 'j6fgkm'
    };

    return this.http.get(url, { 
      params: this.buildHttpParams(mergedParams),
      withCredentials: true
    });
  }

  /**
   * Load Line 1 Data
   * Action code: m10tun
   */
  getLine1Data(
    baseUrl: string,
    params: any,
    globalService: any,
    filterParams?: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject`;
    
    const mergedParams = {
      ...globalService,
      ...(filterParams || {}),
      ...params,
      action_code: 'm10tun'
    };

    return this.http.get(url, { 
      params: this.buildHttpParams(mergedParams),
      withCredentials: true
    });
  }

  /**
   * Get M2 Check Data (for line 11 values)
   * Action code: fj9ugd
   * This determines if line 11 should show Yes or No
   */
  getM2CheckData(
    baseUrl: string,
    params: any,
    globalService: any,
    filterParams?: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject`;
    
    const mergedParams = {
      ...globalService,
      ...(filterParams || {}),
      ...params,
      action_code: 'fj9ugd'
    };

    return this.http.get(url, { 
      params: this.buildHttpParams(mergedParams),
      withCredentials: true
    });
  }

  /**
   * Save Schedule OPG (used for Schedule PG1, NA, and O)
   * Action code: ysbq1g
   * Save operations use POST
   * This is the same endpoint used by all 3 save operations in AngularJS:
   * - saveScheduleGForm (PG1)
   * - saveForm (NA) 
   * - saveScheduleOform (O)
   * 
   * Uses _.merge like Schedule O and original AngularJS:
   * params = _.merge({}, GlobalService.globalParams, filterParams);
   */
  saveScheduleOPG(
    baseUrl: string,
    settings: any,
    details: any,
    globalService: any,
    filterParams: any
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=ysbq1g`;
    
    // Use _.merge like Schedule O service and original AngularJS
    // This ensures deep merge of all properties including checkLockedbj, scenarioCode, etc.
    let params = _.merge({}, globalService || {}, filterParams || {});
    
    params.jsonObj = JSON.stringify(details);
    params.jsonSettings = JSON.stringify(settings);
    params.process_name = 'Schedule_O_P_G_Save';

    return this.http.post(url, this.buildFormData(params), this.getPostHttpOptions());
  }

  /**
   * Reload all form data after save
   * This mimics the getFormData() call in AngularJS after successful save
   */
  reloadAllData(
    baseUrl: string,
    params: any,
    globalService: any,
    filterParams: any
  ) {
    // Returns object with all observables for forkJoin
    return {
      formData: this.getFormData(baseUrl, params, globalService, filterParams),
      schedulePG1: this.getSchedulePG1Data(baseUrl, params, globalService, filterParams),
      scheduleO: this.getScheduleOData(baseUrl, params, globalService, filterParams),
      filerInfo: this.getFilerInfo(baseUrl, params, globalService, filterParams),
      partnerInfo: this.getPartnerInfo(baseUrl, params, globalService, filterParams)
    };
  }
}
